import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getAdminProjects, setAdminProjectAreas, updateProject, deleteProject,
  getAdminAreas, getTeamMembers, exportAdminProjects, previewImportAdminProjects, importAdminProjects,
} from '../../api/client'
import Modal from '../../components/Modal'
import ColumnFilterMulti from '../../components/ColumnFilterMulti'
import PageHeader from '../../components/PageHeader'
import PMSelect from '../../components/PMSelect'
import { detalleError } from '../../components/admin/detalle'
import { formatDate } from '../../utils/weeks'

// Administración > Proyectos: the company's real projects (a scenario's
// copies never show here), with the areas that work on them and how many
// Mantenimiento units each has. Creating one -- and its budgets, materials,
// milestones -- is the full setup page (/admin/proyectos/configurar); here
// the basics and the areas. Name and code are admin's only: Engineering's
// PMs edit dates and status in Ingeniería > Proyectos.

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5'

const ALFA = new Intl.Collator('es', { sensitivity: 'base', numeric: true })
const SORT_INICIAL = { unidades: 'desc', fechas: 'desc' }
const clave = {
  code: p => p.code || '', sap_code: p => p.sap_code || '', name: p => p.name || '', nickname: p => p.nickname || '',
  pm: p => p.po_pm || '', unidades: p => p.unidades || 0, fechas: p => p.start_date || '', estado: p => (p.is_active ? 0 : 1),
}

export default function AdminProyectos() {
  const { t } = useTranslation(['admin', 'common'])
  const navigate = useNavigate()
  const [proyectos, setProyectos] = useState(null)
  const [areas, setAreas] = useState([])
  const [miembros, setMiembros] = useState([])
  const [error, setError] = useState('')
  const [buscar, setBuscar] = useState('')
  const [inactivos, setInactivos] = useState(false)
  const [editando, setEditando] = useState(null)      // proyecto
  const [orden, setOrden] = useState({ key: 'name', dir: 'asc' })      // alphabetical by name to begin with
  const [fPm, setFPm] = useState([])
  const [fAreas, setFAreas] = useState([])
  const [fEstado, setFEstado] = useState([])          // 'activo' | 'inactivo'
  const [fSap, setFSap] = useState([])                // 'con' | 'sin'
  const [seleccion, setSeleccion] = useState(() => new Set())
  const [exportando, setExportando] = useState(false)
  const [importando, setImportando] = useState(false)

  useEffect(() => {
    getAdminAreas().then(setAreas).catch(() => {})
    getTeamMembers().then(ms => setMiembros(Array.isArray(ms) ? ms : [])).catch(() => {})
  }, [])

  const cargar = useCallback(() => (
    getAdminProjects({ include_inactive: true })
      .then(ps => { setProyectos(ps); setError('') })
      .catch(() => setError(t('proyectos.loadFailed')))
  ), [t])
  useEffect(() => { cargar() }, [cargar])

  const txtFiltro = { searchPlaceholder: t('proyectos.filtro.buscar'), clearLabel: t('proyectos.filtro.quitar'), emptyLabel: t('proyectos.filtro.sinOpciones') }
  const opcionesPm = useMemo(() => [...new Set((proyectos || []).map(p => p.po_pm).filter(Boolean))].sort(ALFA.compare), [proyectos])
  const opcionesAreas = useMemo(() => areas.map(a => ({ value: String(a.id), label: a.name })), [areas])

  // Search, header filters and order all work on what is loaded.
  const visibles = useMemo(() => {
    if (!proyectos) return null
    const q = buscar.trim().toLowerCase()
    const lista = proyectos.filter(p => {
      if (fEstado.length ? !fEstado.includes(p.is_active ? 'activo' : 'inactivo') : (!inactivos && !p.is_active)) return false
      if (fPm.length && !fPm.includes(p.po_pm)) return false
      if (fAreas.length && !p.areas.some(a => fAreas.includes(String(a.area_id)))) return false
      if (fSap.length && !fSap.includes(p.sap_code ? 'con' : 'sin')) return false
      return !q || [p.code, p.sap_code, p.name, p.nickname, p.po_pm].some(v => (v || '').toLowerCase().includes(q))
    })
    const f = clave[orden.key]
    const mul = orden.dir === 'asc' ? 1 : -1
    return [...lista].sort((a, b) => {
      const x = f(a), y = f(b)
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : ALFA.compare(String(x), String(y))
      return c * mul || ALFA.compare(a.name || '', b.name || '')
    })
  }, [proyectos, buscar, inactivos, fPm, fAreas, fEstado, fSap, orden])

  const ordenar = (k) => setOrden(o => (o.key === k ? { key: k, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: SORT_INICIAL[k] || 'asc' }))
  const hayFiltros = fPm.length || fAreas.length || fEstado.length || fSap.length || buscar
  const limpiar = () => { setFPm([]); setFAreas([]); setFEstado([]); setFSap([]); setBuscar('') }

  const idsVisibles = (visibles || []).map(p => p.id)
  const todosMarcados = idsVisibles.length > 0 && idsVisibles.every(id => seleccion.has(id))
  const algunoMarcado = idsVisibles.some(id => seleccion.has(id))
  const marcar = (id) => setSeleccion(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const marcarTodos = (on) => setSeleccion(s => { const n = new Set(s); idsVisibles.forEach(id => (on ? n.add(id) : n.delete(id))); return n })

  // Ticked projects go as they are; with none ticked, the ones on screen (search and filters applied).
  const exportar = async () => {
    setExportando(true); setError('')
    try {
      const marcados = idsVisibles.filter(id => seleccion.has(id))
      await exportAdminProjects(marcados.length ? marcados : idsVisibles)
    } catch (e) {
      setError(e.response?.status === 404 ? t('proyectos.exportar.vacio') : t('proyectos.exportar.fallo'))
    }
    setExportando(false)
  }
  const nMarcados = idsVisibles.filter(id => seleccion.has(id)).length

  const eliminar = async (p) => {
    if (!window.confirm(t('proyectos.deleteConfirm', { name: p.name }))) return
    setError('')
    try {
      await deleteProject(p.id)
      setProyectos(ps => ps.filter(x => x.id !== p.id))
      setSeleccion(s => { const n = new Set(s); n.delete(p.id); return n })
    } catch (e) {
      setError(detalleError(e, t('proyectos.deleteFailed')))
    }
  }

  const fechas = (p) => {
    if (!p.start_date && !p.end_date) return <span className="text-gray-300">—</span>
    return `${p.start_date ? formatDate(p.start_date) : '?'} — ${p.end_date ? formatDate(p.end_date) : t('proyectos.enCurso')}`
  }

  const th = (k, extra = '', filtro = null) => (
    <th key={k} className={`px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap ${k === 'unidades' ? 'text-right' : 'text-left'} ${extra}`}
      aria-sort={orden.key === k ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => ordenar(k)} className={`inline-flex items-center gap-1 hover:text-gray-900 ${orden.key === k ? 'text-gray-900' : ''}`}
        title={t('proyectos.ordenar', { col: t(`proyectos.columns.${k}`) })}>
        {t(`proyectos.columns.${k}`)}
        <span className={orden.key === k ? '' : 'invisible'} aria-hidden="true">{orden.key === k && orden.dir === 'desc' ? '▼' : '▲'}</span>
      </button>
      {filtro}
    </th>
  )

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('proyectos.title')} subtitle={t('proyectos.subtitle')}
          actions={(
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setImportando(true)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                ↑ {t('proyectos.importar.boton')}
              </button>
              <button onClick={() => navigate('/admin/proyectos/configurar')}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                {t('proyectos.add')}
              </button>
            </div>
          )} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('proyectos.buscar')}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 bg-white" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={inactivos} onChange={e => setInactivos(e.target.checked)} className="accent-gray-900" />
          {t('proyectos.mostrarInactivos')}
        </label>
        {hayFiltros && <button onClick={limpiar} className="text-sm text-indigo-600 hover:underline">{t('proyectos.filtro.limpiar')}</button>}
        {visibles && <span className="text-xs text-gray-500">{t('proyectos.count', { count: visibles.length })}</span>}
        {nMarcados > 0 && (
          <span className="text-xs text-indigo-700">
            {t('proyectos.exportar.marcados', { count: nMarcados })}{' '}
            <button type="button" onClick={() => setSeleccion(new Set())} className="underline">{t('proyectos.exportar.quitar')}</button>
          </span>
        )}
        <button type="button" onClick={exportar} disabled={exportando || !visibles?.length}
          title={nMarcados ? t('proyectos.exportar.ayudaMarcados') : t('proyectos.exportar.ayudaTodos')}
          className="ml-auto px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40">
          ↓ {exportando ? t('proyectos.exportar.preparando') : nMarcados ? t('proyectos.exportar.botonMarcados', { count: nMarcados }) : t('proyectos.exportar.botonTodos', { count: visibles?.length ?? 0 })}
        </button>
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      {!visibles ? (
        !error && <p className="text-gray-400 text-sm">{t('common:actions.loading')}</p>
      ) : !visibles.length ? (
        <div className="text-center py-12 text-gray-400"><p className="text-sm">{t('proyectos.empty')}</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto lg:overflow-visible">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="pl-5 pr-0 py-3 w-8">
                  <input type="checkbox" className="accent-gray-900" checked={todosMarcados} aria-label={t('proyectos.exportar.marcarTodos')}
                    title={t('proyectos.exportar.marcarTodos')}
                    ref={el => { if (el) el.indeterminate = algunoMarcado && !todosMarcados }}
                    onChange={e => marcarTodos(e.target.checked)} />
                </th>
                {th('code')}
                {th('sap_code', '', (
                  <ColumnFilterMulti label={t('proyectos.filtro.por', { col: t('proyectos.columns.sap_code') })} values={fSap} onChange={setFSap} {...txtFiltro}
                    options={[{ value: 'con', label: t('proyectos.filtro.conSap') }, { value: 'sin', label: t('proyectos.filtro.sinSap') }]} />
                ))}
                {th('name')}
                {th('nickname')}
                {th('pm', '', <ColumnFilterMulti label={t('proyectos.filtro.por', { col: t('proyectos.columns.pm') })} values={fPm} options={opcionesPm} onChange={setFPm} {...txtFiltro} />)}
                <th className="px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap text-left">
                  {t('proyectos.columns.areas')}
                  <ColumnFilterMulti label={t('proyectos.filtro.por', { col: t('proyectos.columns.areas') })} values={fAreas} options={opcionesAreas} onChange={setFAreas} {...txtFiltro} />
                </th>
                {th('unidades')}
                {th('fechas')}
                {th('estado', '', (
                  <ColumnFilterMulti label={t('proyectos.filtro.por', { col: t('proyectos.columns.estado') })} values={fEstado} onChange={setFEstado} {...txtFiltro}
                    options={[{ value: 'activo', label: t('proyectos.activo') }, { value: 'inactivo', label: t('proyectos.inactivo') }]} />
                ))}
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(p => (
                <tr key={p.id} className={`border-b border-gray-50 hover:bg-gray-50/60 last:border-b-0 ${seleccion.has(p.id) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="pl-5 pr-0 py-3 w-8">
                    <input type="checkbox" className="accent-gray-900" checked={seleccion.has(p.id)} onChange={() => marcar(p.id)}
                      aria-label={t('proyectos.exportar.marcar', { name: p.name })} />
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {p.code
                      ? <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{p.code}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {p.sap_code
                      ? <span className="text-xs font-mono bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded">{p.sap_code}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className={`px-5 py-3 font-medium ${p.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                      <button type="button" onClick={() => setEditando(p)} className="hover:underline text-left">{p.name}</button>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{p.nickname || <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{p.po_pm || <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {p.areas.length ? p.areas.map(a => (
                        <span key={a.area_id} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{a.name}</span>
                      )) : <span className="text-xs text-gray-300">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-700"
                    title={p.unidades ? t('proyectos.etiquetaHint', { etiqueta: p.etiqueta }) : undefined}>
                    {p.unidades || <span className="text-gray-300">0</span>}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">{fechas(p)}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {p.is_active
                      ? <span className="text-xs text-emerald-600 font-medium">{t('proyectos.activo')}</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">{t('proyectos.inactivo')}</span>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditando(p)} className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('common:actions.edit')}</button>
                    <Link to={`/admin/proyectos/configurar?id=${p.id}`} className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('proyectos.configurar')}</Link>
                    <Link to={`/projects/${p.id}`} className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('proyectos.verDetalle')}</Link>
                    <button onClick={() => eliminar(p)}
                      disabled={p.unidades > 0}
                      title={p.unidades > 0 ? t('proyectos.conUnidades', { count: p.unidades }) : undefined}
                      className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed">
                      {t('common:actions.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando && (
        <ProyectoModal
          proyecto={editando}
          areas={areas}
          miembros={miembros}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); cargar() }}
        />
      )}
      {importando && <ImportarProyectos onClose={() => setImportando(false)} onDone={cargar} />}
    </div>
  )
}

// Import: pick the Excel, see what it would create and change, then load it.
function ImportarProyectos({ onClose, onDone }) {
  const { t } = useTranslation(['admin', 'common'])
  const [fase, setFase] = useState('elegir')      // elegir | leyendo | previa | cargando | listo
  const [file, setFile] = useState(null)
  const [previa, setPrevia] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [error, setError] = useState('')
  const input = useRef(null)

  const elegir = async (f) => {
    if (!f) return
    setFile(f); setError(''); setFase('leyendo')
    try {
      setPrevia(await previewImportAdminProjects(f)); setFase('previa')
    } catch (e) {
      setError(detalleError(e, t('proyectos.importar.fallo'))); setFase('elegir')
    }
  }
  const cargar = async () => {
    setFase('cargando'); setError('')
    try {
      setResultado(await importAdminProjects(file)); setFase('listo'); onDone()
    } catch (e) {
      setError(detalleError(e, t('proyectos.importar.fallo'))); setFase('previa')
    }
  }
  const campo = (c) => t(`proyectos.importar.campos.${c}`, c)
  const valor = (v) => (v == null || v === '' ? '—' : Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? t('proyectos.activo') : t('proyectos.inactivo')) : String(v))
  const ocupado = fase === 'leyendo' || fase === 'cargando'
  const aplicable = previa && (previa.nuevos > 0 || previa.actualizados > 0)

  return (
    <Modal onClose={ocupado ? () => {} : onClose} title={t('proyectos.importar.titulo')} wide>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{t('proyectos.importar.intro')}</p>
        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

        {(fase === 'elegir' || fase === 'leyendo') && (
          <div>
            <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={e => elegir(e.target.files?.[0])} />
            <button disabled={fase === 'leyendo'} onClick={() => input.current?.click()}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
              {fase === 'leyendo' ? t('proyectos.importar.leyendo') : t('proyectos.importar.elegir')}
            </button>
            <p className="text-xs text-gray-400 mt-3">{t('proyectos.importar.columnas')}</p>
          </div>
        )}

        {(fase === 'previa' || fase === 'cargando') && previa && (
          <>
            <h3 className="text-sm font-semibold text-gray-700">{t('proyectos.importar.previa')} <span className="font-normal text-gray-400">· {previa.archivo}</span></h3>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[['nuevos', previa.nuevos, 'text-emerald-600'], ['actualizados', previa.actualizados, 'text-amber-600'],
                ['sin_cambios', previa.sin_cambios, 'text-gray-700'], ['a_revisar', previa.a_revisar, previa.a_revisar ? 'text-red-600' : 'text-gray-700']].map(([k, v, tone]) => (
                <div key={k} className="rounded-lg border px-2 py-2">
                  <div className={`text-xl font-semibold ${tone}`}>{v}</div>
                  <div className="text-xs text-gray-500">{t(`proyectos.importar.${k}`)}</div>
                </div>
              ))}
            </div>
            {previa.detalle.length > 0 && (
              <ul className="border rounded-lg max-h-64 overflow-y-auto text-xs divide-y">
                {previa.detalle.map((d, i) => (
                  <li key={i} className="px-3 py-1.5 flex gap-2">
                    <span className={`shrink-0 px-1.5 rounded text-[11px] font-medium ${d.tipo === 'nuevo' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {t(d.tipo === 'nuevo' ? 'proyectos.importar.nuevo' : 'proyectos.importar.actualiza')}
                    </span>
                    <span className="min-w-0">
                      <span className="font-medium text-gray-800">{d.codigo ? `${d.codigo} · ` : ''}{d.nombre}</span>
                      <span className="block text-gray-500 break-words">
                        {Object.entries(d.cambios).filter(([k]) => !(d.tipo === 'nuevo' && (k === 'name' || k === 'code'))).map(([k, v]) => (
                          d.tipo === 'nuevo' ? `${campo(k)}: ${valor(v)}` : `${campo(k)}: ${valor(d.antes[k])} → ${valor(v)}`
                        )).concat(d.areas ? [`${campo('areas')}: ${d.areas.length}`] : []).join(' · ')}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {previa.casos_a_revisar.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-1">{t('proyectos.importar.casos')}</h4>
                <ul className="border border-red-100 rounded-lg max-h-40 overflow-y-auto text-xs divide-y">
                  {previa.casos_a_revisar.map((c, i) => (
                    <li key={i} className="px-3 py-1.5 text-gray-700">
                      <span className="font-medium">{t('proyectos.importar.fila', { n: c.fila })} · {c.proyecto || '—'}:</span> {c.problemas.join('; ')}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-400 mt-1">{t('proyectos.importar.casosNota')}</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} disabled={fase === 'cargando'} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">{t('common:actions.cancel')}</button>
              <button onClick={cargar} disabled={fase === 'cargando' || !aplicable}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
                {fase === 'cargando' ? t('proyectos.importar.cargando') : t('proyectos.importar.confirmar')}
              </button>
            </div>
          </>
        )}

        {fase === 'listo' && resultado && (
          <>
            <p className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm">{t('proyectos.importar.listo', { creados: resultado.creados, actualizados: resultado.actualizados })}</p>
            <div className="flex justify-end"><button onClick={onClose} className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm">{t('proyectos.importar.cerrar')}</button></div>
          </>
        )}
      </div>
    </Modal>
  )
}

function ProyectoModal({ proyecto, areas, miembros, onClose, onSaved }) {
  const { t } = useTranslation(['admin', 'common'])
  const [form, setForm] = useState(() => ({
    name: proyecto.name, code: proyecto.code || '', nickname: proyecto.nickname || '',
    po_pm: proyecto.po_pm || '', pm_id: proyecto.pm_id ?? null, is_active: proyecto.is_active,
    start_date: proyecto.start_date || '', end_date: proyecto.end_date || '',
    areas: proyecto.areas.map(a => a.area_id),
  }))
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }))

  // Active areas, plus an inactive one the project still has.
  const areasVisibles = areas.filter(a => a.is_active || form.areas.includes(a.id))
  const toggleArea = (id) => setForm(f => ({
    ...f, areas: f.areas.includes(id) ? f.areas.filter(x => x !== id) : [...f.areas, id],
  }))

  const guardar = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) { setError(t('proyectos.form.nameRequired')); return }
    setGuardando(true)
    setError('')
    try {
      await updateProject(proyecto.id, {
        name: form.name.trim(),
        code: form.code.trim() || null,
        nickname: form.nickname.trim() || null,
        po_pm: (form.po_pm || '').trim() || null,
        pm_id: form.pm_id ?? null,
        is_active: form.is_active,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      })
      const antes = proyecto.areas.map(a => a.area_id).sort().join(',')
      if ([...form.areas].sort().join(',') !== antes) await setAdminProjectAreas(proyecto.id, form.areas)
      onSaved()
    } catch (err) {
      setError(detalleError(err, t('proyectos.saveFailed')))
      setGuardando(false)
    }
  }

  return (
    <Modal title={t('proyectos.form.editTitle', { name: proyecto.name })} onClose={onClose} wide>
      <form onSubmit={guardar} className="space-y-5">
        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>{t('proyectos.form.code')}</label>
            <input className={`${inputCls} font-mono`} value={form.code} onChange={set('code')} placeholder="2026-047" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t('proyectos.form.name')} *</label>
            <input autoFocus className={inputCls} value={form.name} onChange={set('name')} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('proyectos.form.nickname')}</label>
            <input className={inputCls} value={form.nickname} onChange={set('nickname')} />
            <p className="text-xs text-gray-400 mt-1">{t('proyectos.form.nicknameHint')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('proyectos.form.pm')}</label>
            <PMSelect value={form.pm_id} nombre={form.po_pm} members={miembros}
              onChange={(id, nombre) => setForm(f => ({ ...f, pm_id: id, po_pm: nombre || '' }))} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('proyectos.form.startDate')}</label>
            <input type="date" className={inputCls} value={form.start_date} onChange={set('start_date')} />
          </div>
          <div>
            <label className={labelCls}>{t('proyectos.form.endDate')}</label>
            <input type="date" className={inputCls} value={form.end_date} onChange={set('end_date')} />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="mt-0.5 accent-gray-900" checked={form.is_active}
            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          <span>
            {t('proyectos.form.isActive')}
            <span className="block text-xs text-gray-400">{t('proyectos.form.isActiveHint')}</span>
          </span>
        </label>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('proyectos.form.areas')}</h3>
          {!areasVisibles.length ? (
            <p className="text-xs text-gray-400">{t('proyectos.form.sinAreas')}</p>
          ) : (
            <div className="border border-gray-200 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-x-4 px-3 py-2">
              {areasVisibles.map(a => (
                <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-1">
                  <input type="checkbox" className="accent-gray-900" checked={form.areas.includes(a.id)} onChange={() => toggleArea(a.id)} />
                  {a.name}
                  {!a.is_active && <span className="text-[10px] text-gray-400">({t('areas.inactiva')})</span>}
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-1.5">{t('proyectos.form.areasHint')}</p>
        </section>

        <div className="flex items-center justify-between gap-2 pt-2">
          <Link to={`/admin/proyectos/configurar?id=${proyecto.id}`} className="text-xs font-medium text-gray-500 hover:text-gray-900 underline">
            {t('proyectos.form.configuracionCompleta')}
          </Link>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
              {t('common:actions.cancel')}
            </button>
            <button type="submit" disabled={guardando}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
              {guardando ? t('common:actions.saving') : t('common:actions.save')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
